import { z } from 'zod';

const localizedStringSchema = z.union([
    z.string(),
    z.record(z.string()),
]).optional();

export const INTEGRATION_CONNECTION_STATUSES = ['draft', 'active', 'inactive', 'error'];
export const INTEGRATION_INVOCATION_STATUSES = ['success', 'failed', 'timeout', 'blocked'];
export const INTEGRATION_HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'];

const credentialRecordSchema = z.record(z.string()).optional().default({});

export const CreateIntegrationConnectionSchema = z.object({
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(2000).optional(),
    providerKey: z.string().trim().min(1).max(100),
    providerVersion: z.number().int().positive().optional(),
    status: z.enum(INTEGRATION_CONNECTION_STATUSES).optional().default('draft'),
    credentials: credentialRecordSchema,
    config: z.record(z.unknown()).optional().default({}),
    customFields: z.record(z.unknown()).optional(),
    allowPrivateNetwork: z.boolean().optional(),
});

export const UpdateIntegrationConnectionSchema = CreateIntegrationConnectionSchema.partial().omit({
    providerKey: true,
});

const responseColumnSchema = z.object({
    key: z.string(),
    label: localizedStringSchema,
});

const responseViewSchema = z.object({
    key: z.string(),
    label: localizedStringSchema,
    mode: z.enum(['array', 'object']).default('array'),
    rowsPath: z.string().optional(),
    columns: z.array(responseColumnSchema).optional(),
    drillDown: z.record(z.unknown()).optional(),
});

export const IntegrationResponseMappingSchema = z.object({
    mode: z.enum(['array', 'object', 'multi']).default('object'),
    rowsPath: z.string().optional(),
    columns: z.array(responseColumnSchema).optional(),
    views: z.array(responseViewSchema).optional(),
}).optional();

export const IntegrationPathParamSchema = z.object({
    key: z.string().min(1),
    label: localizedStringSchema,
    required: z.boolean().optional(),
    type: z.enum(['string', 'number', 'boolean']).optional(),
    help: localizedStringSchema,
});

export const IntegrationQueryParamSchema = z.object({
    key: z.string().min(1),
    label: localizedStringSchema,
    required: z.boolean().optional(),
    type: z.enum(['string', 'number', 'boolean']).optional(),
    help: localizedStringSchema,
});

export const CreateIntegrationEndpointSchema = z.object({
    connectionId: z.string().min(1),
    key: z.string().trim().min(1).max(100).regex(/^[a-z0-9][a-z0-9_-]*$/i),
    label: localizedStringSchema,
    description: localizedStringSchema,
    method: z.enum(INTEGRATION_HTTP_METHODS).default('GET'),
    path: z.string().trim().min(1).max(500).refine(
        (p) => !/^https?:\/\//i.test(p) && !p.startsWith('//'),
        { message: 'Absolute URLs are not allowed; use a path under the connection baseUrl' },
    ),
    headers: z.record(z.string()).optional().default({}),
    category: z.string().trim().max(100).optional(),
    pathParams: z.array(IntegrationPathParamSchema).optional().default([]),
    queryParams: z.array(IntegrationQueryParamSchema).optional().default([]),
    responseMapping: IntegrationResponseMappingSchema,
    isActive: z.boolean().optional().default(true),
});

export const UpdateIntegrationEndpointSchema = CreateIntegrationEndpointSchema.partial().omit({
    connectionId: true,
    key: true,
});

const providerEndpointDefSchema = z.object({
    key: z.string().trim().min(1).max(100).regex(/^[a-z0-9][a-z0-9_-]*$/i),
    label: localizedStringSchema,
    description: localizedStringSchema,
    method: z.enum(INTEGRATION_HTTP_METHODS).optional().default('GET'),
    path: z.string().trim().min(1).max(500).refine(
        (p) => !/^https?:\/\//i.test(p) && !p.startsWith('//'),
        { message: 'Absolute URLs are not allowed; use a path under the connection baseUrl' },
    ),
    category: z.string().trim().max(100).optional(),
    pathParams: z.array(IntegrationPathParamSchema).optional().default([]),
    queryParams: z.array(IntegrationQueryParamSchema).optional().default([]),
    pagination: z.object({
        type: z.enum(['offset', 'page', 'token', 'none']).optional().default('none'),
        pageParam: z.string().optional(),
        pageSizeParam: z.string().optional(),
        offsetParam: z.string().optional(),
        limitParam: z.string().optional(),
        tokenParam: z.string().optional(),
        client: z.boolean().optional(),
    }).optional(),
    responseMapping: IntegrationResponseMappingSchema,
    drillDown: z.record(z.unknown()).optional(),
});

const providerCapabilitiesDefSchema = z.object({
    explore: z.object({
        enabled: z.boolean().optional().default(true),
        modes: z.array(z.string()).optional(),
        maxPageSize: z.number().int().positive().optional(),
    }).optional(),
    pull: z.object({
        enabled: z.boolean().optional().default(true),
        resources: z.array(z.object({
            key: z.string(),
            label: localizedStringSchema.optional(),
        })).optional().default([]),
    }).optional(),
    mappingImport: z.object({
        enabled: z.boolean().optional().default(true),
        previewRequired: z.boolean().optional(),
        maxBatchSize: z.number().int().positive().optional(),
    }).optional(),
    scheduledSync: z.object({
        enabled: z.boolean().optional().default(false),
        cursorTypes: z.array(z.string()).optional(),
    }).optional(),
    actions: z.array(z.unknown()).optional(),
    inboundWebhooks: z.object({
        enabled: z.boolean().optional().default(false),
    }).optional(),
}).optional().default({});

const mappingTemplateSchema = z.object({
    key: z.string().min(1),
    name: z.union([z.string(), z.record(z.string())]),
    description: localizedStringSchema,
    resourceKey: z.string().min(1),
    targetEntityType: z.enum(['Asset', 'Vulnerability']),
    externalIdSourcePath: z.string().min(1),
    fieldMappings: z.array(z.unknown()).optional().default([]),
    referenceMappings: z.array(z.unknown()).optional().default([]),
});

export const CreateIntegrationProviderDefinitionSchema = z.object({
    key: z.string().trim().min(1).max(100).regex(/^[a-z0-9][a-z0-9_-]*$/i),
    label: localizedStringSchema,
    description: localizedStringSchema,
    authScheme: z.enum([
        'api-keys-header',
        'bearer',
        'basic',
        'api-key-header',
        'header-template',
        'custom',
    ]).default('bearer'),
    authConfig: z.object({
        headerName: z.string().optional(),
        headerValueTemplate: z.string().optional(),
        headerTemplates: z.record(z.string()).optional(),
        headers: z.record(z.string()).optional(),
    }).optional().default({}),
    credentialFields: z.array(z.object({
        key: z.string(),
        label: localizedStringSchema,
        required: z.boolean().optional(),
        secret: z.boolean().optional().default(true),
    })).optional().default([]),
    configFields: z.array(z.object({
        key: z.string(),
        label: localizedStringSchema,
        required: z.boolean().optional(),
        type: z.string().optional(),
        default: z.unknown().optional(),
    })).optional().default([]),
    endpoints: z.array(providerEndpointDefSchema).optional().default([]),
    capabilities: providerCapabilitiesDefSchema,
    mappingTemplates: z.array(mappingTemplateSchema).optional().default([]),
    version: z.number().int().positive().optional().default(1),
    status: z.enum(['draft', 'published', 'retired']).optional(),
    isActive: z.boolean().optional().default(true),
});

export const UpdateIntegrationProviderDefinitionSchema = CreateIntegrationProviderDefinitionSchema.partial().omit({
    key: true,
});

export const InvokeEndpointQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(25),
    search: z.string().max(500).optional().default(''),
    sortBy: z.string().max(100).optional(),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});

export const IntegrationConnectionListQuerySchema = z.object({
    page: z.coerce.number().int().min(1).optional().default(1),
    pageSize: z.coerce.number().int().min(1).max(100).optional().default(20),
    search: z.string().max(500).optional().default(''),
    sortBy: z.enum(['name', 'providerKey', 'status', 'lastTestedAt', 'updatedAt', 'createdAt']).optional().default('updatedAt'),
    sortOrder: z.enum(['asc', 'desc']).optional().default('desc'),
});
